import { apiClient, baseUrl } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Krok K0e — ukoly, ktere u zakazky dela CLOVEK ve verejnych registrech
 * (nahlizeni do KN, DPN vypis, ISIR, CEE). Pilot bezi rucne do prvnich plateb.
 *
 * Typy chodi z vygenerovaneho OpenAPI klienta, ne z rucniho interface: tvar
 * odpovedi drzi kontrakt API, ne domluva mezi dvema soubory.
 *
 * NAHRANI PODKLADU tudy NEJDE pres `apiClient`: je to multipart s binarnim
 * telem (PNG/PDF), takze se pouziva `fetch` se stejnym base URL a cookies.
 */

export type HumanTask =
  ApiPaths['/v1/duediligence/cases/{id}/human-tasks']['get']['responses']['200']['content']['application/json']['items'][number];

export type HumanTaskDocument = HumanTask['documents'][number];

export async function listHumanTasks(caseId: string): Promise<HumanTask[]> {
  const result = await unwrap(
    apiClient.GET('/v1/duediligence/cases/{id}/human-tasks', {
      params: { path: { id: caseId } },
    }),
  );
  return result.items;
}

/** Potvrzeni vytezenych faktu - jeden klik, ktery povysi duveru (K0e bod 4). */
export async function confirmHumanTask(caseId: string, taskId: string, note?: string): Promise<void> {
  await unwrap(
    apiClient.POST('/v1/duediligence/cases/{id}/human-tasks/{taskId}/confirm', {
      params: { path: { id: caseId, taskId } },
      body: note === undefined ? {} : { note },
    }),
  );
}

/** Clovek ukol odmitne (typicky placeny dotaz, ktery klient nechce). */
export async function skipHumanTask(caseId: string, taskId: string, note?: string): Promise<void> {
  await unwrap(
    apiClient.POST('/v1/duediligence/cases/{id}/human-tasks/{taskId}/skip', {
      params: { path: { id: caseId, taskId } },
      body: note === undefined ? {} : { note },
    }),
  );
}

export interface HumanTaskUploadResult {
  /** `true` = soubor opravdu pribyl; `false` = tentyz uz u ukolu lezel. */
  readonly created: boolean;
  /**
   * Krok NAP8 A20: `true` = ukol ma STROJOVY zdroj (`isir.ws.osoba`), takze
   * podklad je PRILOHA ukolu a zadna fakta z nej nevznikaji. Obrazovka to
   * musi rict — jinak by clovek cekal, ze se odpoved zapsala sama.
   */
  readonly attachmentOnly: boolean;
}

/**
 * Nahrani podkladu (multipart).
 */
export async function uploadHumanTaskDocument(
  caseId: string,
  taskId: string,
  file: File,
): Promise<HumanTaskUploadResult> {
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await fetch(
    `${baseUrl}/v1/duediligence/cases/${caseId}/human-tasks/${taskId}/document`,
    { method: 'POST', credentials: 'include', body: form },
  );
  if (!response.ok) {
    // Telo je problem+json; detail se vypisuje uzivateli, proto se nezahazuje.
    const problem = (await response.json().catch(() => null)) as
      | { detail?: string; title?: string }
      | null;
    throw new Error(problem?.detail ?? problem?.title ?? `HTTP ${response.status}`);
  }
  const payload = (await response.json().catch(() => null)) as
    | { attachmentOnly?: boolean }
    | null;
  return {
    created: response.status === 201,
    attachmentOnly: payload?.attachmentOnly === true,
  };
}

/**
 * Krok CEE1 bod 2a — exekuce u vlastniku nad stropem Kompletu. `null` = nic
 * neceka. Potvrzeni NIC neplati: kredit se odecte az u dotazu v dalsim behu.
 */
export type CeeExtraOwnersOffer = NonNullable<
  ApiPaths['/v1/duediligence/cases/{id}/cee/extra-owners']['get']['responses']['200']['content']['application/json']['offer']
>;

export async function getCeeExtraOwnersOffer(caseId: string): Promise<CeeExtraOwnersOffer | null> {
  const result = await unwrap(
    apiClient.GET('/v1/duediligence/cases/{id}/cee/extra-owners', {
      params: { path: { id: caseId } },
    }),
  );
  return result.offer;
}

/**
 * Krok QAFIX3 (nalez N05) — IDENTITA VLASTNIKU ZADANA CLOVEKEM.
 *
 * Endpoint existuje od kroku P1REP3, ale zadna obrazovka ho nevolala: identitu
 * slo zapsat jen pres API. `caseId` je `dd_cases.id` (tyz identifikator jako
 * u ostatnich volani v tomhle modulu) — makler ho nikdy nevidi, dohleda ho
 * `loadCaseDetail` (`resolveAuditCase`) v `dd-api.ts`.
 */
type OwnerIdentityEndpoint = ApiPaths['/v1/duediligence/cases/{id}/owner-identity']['post'];
export type OwnerIdentityBody = NonNullable<OwnerIdentityEndpoint['requestBody']>['content']['application/json'];
export type OwnerIdentityResult = OwnerIdentityEndpoint['responses']['201']['content']['application/json'];

export async function recordOwnerIdentity(
  caseId: string,
  body: OwnerIdentityBody,
): Promise<OwnerIdentityResult> {
  return unwrap(
    apiClient.POST('/v1/duediligence/cases/{id}/owner-identity', {
      params: { path: { id: caseId } },
      body,
    }),
  );
}

export async function approveCeeExtraOwners(caseId: string, owners: number): Promise<void> {
  await unwrap(
    apiClient.POST('/v1/duediligence/cases/{id}/cee/extra-owners', {
      params: { path: { id: caseId } },
      body: { owners },
    }),
  );
}
